import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useSetupStore } from '@up2promo-setup-frontend/core/store/setup.store';
import { useSetupFlow } from '@up2promo-setup-frontend/core/hooks/use-setup-flow';
import { usesUrlSetupFlow } from '@up2promo-setup-frontend/core/constants/setup-product-types';
import { ProductType } from '@up2promo-setup-frontend/core/types/product-type.enum';

/**
 * Hook to calculate step information from current route.
 * Used for progress calculation and header display.
 */
export function useRouteStepInfo() {
  const location = useLocation();
  const type = useSetupStore((state) => state.type);
  const { initialType } = useSetupFlow();

  const routeStepMap = useMemo(() => {
    const steps: string[] = ['welcome', 'email'];

    // Add select-type only if no initialType
    if (!initialType) {
      steps.push('select-type');
    }

    // Add input or custom-url based on type (only if type is set)
    if (usesUrlSetupFlow(type)) {
      steps.push('custom-url');
    } else if (type) {
      // Only add input/address/select-suggestion if type is set and not CUSTOM
      steps.push('input');

      // Add address only for Google type
      if (type === ProductType.GOOGLE) {
        steps.push('address');
      }

      // Add select-suggestion for non-custom types
      steps.push('select-suggestion');
    } else {
      // Type not chosen yet: reserve the longest remaining path (Google)
      // so progress does not jump backward when steps are added after selection.
      steps.push('input', 'address', 'select-suggestion');
    }

    // Final step
    steps.push('congrats');

    return steps;
  }, [type, initialType]);

  const currentRoute = useMemo(() => {
    const pathParts = location.pathname.split('/');
    return pathParts[pathParts.length - 1] || 'welcome';
  }, [location.pathname]);

  const currentStepIndex = useMemo(() => {
    const index = routeStepMap.indexOf(currentRoute);
    // If route not found (e.g., type not set yet), default to 0 to prevent negative values
    return index >= 0 ? index : 0;
  }, [routeStepMap, currentRoute]);

  const maxSteps = routeStepMap.length;

  return {
    currentRoute,
    currentStepIndex,
    maxSteps,
    routeStepMap,
  };
}
